import type { BillingTier, Cohort, SubscriptionStatus } from '@parsleyai/billing'
import type { Timestamp } from 'firebase/firestore'
import type { BaseSliceState } from '../../types'

/**
 * Why the last payment failed. `requiresAction` is the field that matters: a declined or expired card
 * needs replacing, a 3D Secure challenge does not — the card works and the user must confirm with their bank.
 */
export interface BillingPaymentFailure {
  failedAtSeconds: number
  code: string | null
  declineCode: string | null
  requiresAction: boolean
}

/** Enough of a card to name it back to the user. Mirrors the server type. */
export interface BillingPaymentMethod {
  brand: string
  last4: string
  expMonth: number
  expYear: number
}

/**
 * The client's view of `/users/{uid}/billing/subscription`. Mirrored by hand from the server type rather
 * than shared, because the `Timestamp` types differ; the predicate is what's shared, not the shape.
 */
export interface BillingSubscriptionDoc {
  stripeCustomerId: string
  stripeSubscriptionId: string | null
  tier: BillingTier
  status: SubscriptionStatus
  cohort: Cohort
  trialEnd: Timestamp | null
  currentPeriodEnd: Timestamp | null
  cancelAtPeriodEnd: boolean
  /**
   * The card that will be charged, or `null` when none is on file. Server-written — the app can't derive
   * it, since adding a card changes no subscription status.
   */
  paymentMethod: BillingPaymentMethod | null
  /** Why the last payment failed, or null. Drives whether past_due says "replace" or "confirm". */
  paymentFailure: BillingPaymentFailure | null
  pastDueSince: Timestamp | null
  updatedAt: Timestamp
}

/**
 * Where the slice is in its lifecycle. Three states, not two — the distinction between "we don't
 * know yet" and "there genuinely isn't one" drives different UI.
 */
export enum BillingPhase {
  /** No snapshot yet. */
  Loading = 'loading',
  /**
   * Snapshot arrived, document absent. Normal and brief for a new account — provisioning trails the profile
   * doc by a Stripe round trip plus a webhook. Post-backfill an absent document always means provisioning.
   */
  Provisioning = 'provisioning',
  Ready = 'ready'
}

export interface BillingState extends BaseSliceState {
  subscription: BillingSubscriptionDoc | null
  phase: BillingPhase
  /**
   * When the slice first saw an absent document, in epoch millis; `null` otherwise. Set once on entering
   * Provisioning and not refreshed by later absent snapshots, so the elapsed time is real.
   */
  provisioningSinceMs: number | null
}

export enum BillingActionTypes {
  SET_SUBSCRIPTION = 'billing/set',
  SET_ERROR = 'billing/error',
  CLEAR = 'billing/clear'
}

export type BillingAction =
  | { type: BillingActionTypes.SET_SUBSCRIPTION; payload: BillingSubscriptionDoc | null; nowMs: number }
  | { type: BillingActionTypes.SET_ERROR; payload: string }
  | { type: BillingActionTypes.CLEAR }
